import { generateClient } from 'aws-amplify/api';
import { StorageTier } from '../API';

const client = generateClient();

// Custom query to get all storage tiers
const listStorageTierConfigs = /* GraphQL */ `
  query ListStorageTierConfigs {
    listStorageTierConfigs {
      items {
        id
        tier
        name
        storageBytes
        pricePerMonth
        features
        isDefault
        version
        createdAt
        updatedAt
      }
    }
  }
`;

// TypeScript interface for storage tier config
export interface StorageTierConfig {
    id: string;
    tier: StorageTier;
    name: string;
    storageBytes: number;
    pricePerMonth: number;
    features: string[];
    isDefault: boolean;
    version: number;
    createdAt?: string;
    updatedAt?: string;
}

// Default tiers as fallback if API fails
const defaultTiers: StorageTierConfig[] = [
    {
        id: 'FREE_v1',
        tier: StorageTier.FREE,
        name: 'Free',
        storageBytes: 1 * 1024 * 1024 * 1024, // 1GB
        pricePerMonth: 0,
        features: [
            '1GB storage space',
            'Unlimited trips',
            'Unlimited members',
            'Standard photo quality'
        ],
        isDefault: true,
        version: 1
    },
    {
        id: 'BASIC_v1',
        tier: StorageTier.BASIC,
        name: 'Basic',
        storageBytes: 10 * 1024 * 1024 * 1024, // 10GB
        pricePerMonth: 2.99,
        features: [
            '10GB storage space',
            'Unlimited trips',
            'Unlimited members',
            'Standard photo quality'
        ],
        isDefault: false,
        version: 1
    },
    {
        id: 'PREMIUM_v1',
        tier: StorageTier.PREMIUM,
        name: 'Premium',
        storageBytes: 100 * 1024 * 1024 * 1024, // 100GB
        pricePerMonth: 4.99,
        features: [
            '100GB storage space',
            'Unlimited trips',
            'Unlimited members',
            'Original photo quality',
            'Advanced photo organization'
        ],
        isDefault: false,
        version: 1
    },
    {
        id: 'PROFESSIONAL_v1',
        tier: StorageTier.PROFESSIONAL,
        name: 'Professional',
        storageBytes: 500 * 1024 * 1024 * 1024, // 500GB
        pricePerMonth: 9.99,
        features: [
            '500GB storage space',
            'Unlimited trips',
            'Unlimited members',
            'Original photo quality',
            'Advanced photo organization',
            'Priority support'
        ],
        isDefault: false,
        version: 1
    }
];

// Cache for tiers to avoid excessive API calls
let cachedTiers: StorageTierConfig[] | null = null;
let cacheTimestamp = 0;
const CACHE_TTL = 1000 * 60 * 10; // 10 minutes

// Get storage tier configurations from the API
export const getStorageTierConfigs = async (forceRefresh = false): Promise<StorageTierConfig[]> => {
    const now = Date.now();

    // Use cache if available and not expired
    if (!forceRefresh && cachedTiers && now - cacheTimestamp < CACHE_TTL) {
        return cachedTiers;
    }
    try {
        const response: any = await client.graphql({
            query: listStorageTierConfigs,
            authMode: "userPool"
        });
        if (response.data.listStorageTierConfigs?.items?.length > 0) {
            const allTiers = response.data.listStorageTierConfigs.items;

            // Find the highest version number
            let highestVersion = 0;
            allTiers.forEach((tier: StorageTierConfig) => {
                if (tier.version > highestVersion) {
                    highestVersion = tier.version;
                }
            });

            // Filter to only include tiers from the latest version
            const latestTiers = allTiers.filter(
                (tier: StorageTierConfig) => tier.version === highestVersion
            );

            // Sort by price
            const sortedTiers = latestTiers.sort(
                (a: StorageTierConfig, b: StorageTierConfig) => a.pricePerMonth - b.pricePerMonth
            );

            // Update cache
            cachedTiers = sortedTiers;
            cacheTimestamp = now;
            return sortedTiers;
        }

        // If no tiers found, return defaults
        return [];
    } catch (error) {
        console.error('Error fetching storage tier configs:', error);
        // Return default tiers as fallback
        return [];
    }
};

// Get a single tier config by tier name
export const getStorageTierByName = async (tierName: string): Promise<StorageTierConfig> => {
    const tiers = await getStorageTierConfigs();
    const tier = tiers.find(t => t.tier === tierName);
    return tier || tiers.find(t => t.isDefault) || tiers[0];
};

// Get storage limit for a tier
export const getStorageLimitForTier = async (tierName: string): Promise<number> => {
    const tier = await getStorageTierByName(tierName);
    return tier.storageBytes;
};

export const getStoragePriceForTier = async (tierName: string): Promise<number> => {
    const tier = await getStorageTierByName(tierName);
    return tier.pricePerMonth;
};